<!--
  DemographicFilterDropdowns - Optional demographic filter dropdowns.

  Renders race/ethnicity and/or language multi-select dropdowns backed by
  a configurable API endpoint. Designed to be mounted by Worklist_FilterBar
  or any other filter host.

  Props:
    enabledFilters:   Array of filter keys to render (e.g. ['race_ethnicity', 'language'])
    optionsEndpoint:  API path to fetch dropdown options from.

  Emits:
    @change({ race_ethnicity: [], languages: [] })
-->
<template>
  <div v-if="showRaceEthnicity" class="max-w-48">
    <label class="block text-xs font-medium text-gray-600 mb-1">Race/Ethnicity</label>
    <Standard_Multiple_Dropdown
      v-model="selectedRaceEthnicity"
      :options="raceEthnicityOptions"
      placeholder="All Races"
      @change="emitChange"
    />
  </div>

  <div v-if="showLanguage" class="max-w-48">
    <label class="block text-xs font-medium text-gray-600 mb-1">Language</label>
    <Standard_Multiple_Dropdown
      v-model="selectedLanguages"
      :options="languageOptions"
      placeholder="All Languages"
      @change="emitChange"
    />
  </div>
</template>

<script>
import Standard_Multiple_Dropdown from '@/components/widgetcomponents/Dropdown_Selector/Standard_Multiple_Dropdown.vue';

const CMS_RACE_FALLBACK = [
  { id: '1', name: 'White' },
  { id: '2', name: 'Black/African American' },
  { id: '3', name: 'Other' },
  { id: '4', name: 'Asian/Pacific Islander' },
  { id: '5', name: 'Hispanic' },
  { id: '6', name: 'Unknown/Not Specified' },
];

export default {
  name: 'DemographicFilterDropdowns',
  components: { Standard_Multiple_Dropdown },
  props: {
    enabledFilters: {
      type: Array,
      default: () => []
      // Valid keys: 'race_ethnicity', 'language'
    },
    optionsEndpoint: {
      type: String,
      default: 'api/phm/demographic-filter-options/'
    }
  },
  emits: ['change'],
  data() {
    return {
      selectedRaceEthnicity: [],
      selectedLanguages: [],
      raceEthnicityOptions: [],
      languageOptions: [],
      loaded: false
    };
  },
  computed: {
    showRaceEthnicity() {
      return this.loaded && this.enabledFilters.includes('race_ethnicity');
    },
    showLanguage() {
      return this.loaded && this.enabledFilters.includes('language');
    }
  },
  methods: {
    async loadOptions() {
      try {
        const response = await this.$axios.get(this.optionsEndpoint);
        this.raceEthnicityOptions = response.data.race_ethnicity || [];
        this.languageOptions = response.data.languages || [];
        this.loaded = true;
      } catch (error) {
        if (error.response && error.response.status === 403) {
          // User doesn't have access — hide filters silently
          this.loaded = false;
          return;
        }
        // Non-auth error — use fallback for race, skip language
        this.raceEthnicityOptions = CMS_RACE_FALLBACK;
        this.languageOptions = [];
        this.loaded = true;
      }
    },

    emitChange() {
      this.$emit('change', {
        race_ethnicity: this.selectedRaceEthnicity,
        languages: this.selectedLanguages
      });
    },

    getFilterTags() {
      const tags = [];
      for (const raceId of this.selectedRaceEthnicity) {
        const race = this.raceEthnicityOptions.find(r => r.id === raceId);
        if (race) {
          tags.push({ key: `demo-race-${raceId}`, label: `Race: ${race.name}` });
        }
      }
      for (const langId of this.selectedLanguages) {
        const lang = this.languageOptions.find(l => l.id === langId);
        if (lang) {
          tags.push({ key: `demo-lang-${langId}`, label: `Language: ${lang.name}` });
        }
      }
      return tags;
    },

    removeFilter(key) {
      if (key.startsWith('demo-race-')) {
        const raceId = key.replace('demo-race-', '');
        this.selectedRaceEthnicity = this.selectedRaceEthnicity.filter(id => id !== raceId);
        this.emitChange();
        return true;
      }
      if (key.startsWith('demo-lang-')) {
        const langId = key.replace('demo-lang-', '');
        this.selectedLanguages = this.selectedLanguages.filter(id => id !== langId);
        this.emitChange();
        return true;
      }
      return false;
    },

    clearAll() {
      this.selectedRaceEthnicity = [];
      this.selectedLanguages = [];
    },

    hasSelections() {
      return this.selectedRaceEthnicity.length > 0 || this.selectedLanguages.length > 0;
    }
  },
  async mounted() {
    if (this.enabledFilters.length > 0) {
      await this.loadOptions();
    }
  }
};
</script>
